<template>
    <v-dialog v-if="rpManager" v-model="transferDialog" width="500" persistent>
        <template v-slot:activator="{ on, attrs }">
            <div class="d-flex align-center flex-column">
                <v-btn v-bind="attrs" v-on="on" large fab elevation="0" aria-label="Transfer credits">
                    <v-icon large>mdi-transfer</v-icon>
                </v-btn>
                <span class="mt-2">Transfer credits</span>
            </div>
        </template>
        <v-card flat>
            <v-card-title class="secondary--text flex-nowrap align-start">
                Transfer credits
                <v-spacer></v-spacer>
                <v-btn icon :disabled="transferringCredits" aria-label="Close" @click="cancel()"><v-icon>clear</v-icon></v-btn>
            </v-card-title>
            <v-card-text>
                <v-form class="mt-4" @submit.prevent="transferCredits()">
                    <ResourcePoolSelector v-model="transferRecipient" :items="managedResourcePools" label="Recipient" :disabled="transferringCredits" />
                    <v-text-field
                        :value="amount"
                        outlined
                        dense
                        prefix="AC"
                        persistent-hint
                        :disabled="transferringCredits"
                        :hint="transferCreditHint"
                        :rules="[() => amountNumber <= roundedBalance || `Can't be more than available balance.`]"
                        @input="setAmount">
                        <template v-slot:append>
                            <v-btn small text :disabled="transferringCredits || credits <= 0" @click="sendAll()">MAX</v-btn>
                        </template>
                    </v-text-field>
                </v-form>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="secondary" text :disabled="transferringCredits" @click="cancel()">cancel</v-btn>
                <v-btn color="primary" text :disabled="!canTransfer" :loading="transferringCredits" @click="transferCredits()">transfer</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import createNumberMask from 'text-mask-addons/dist/createNumberMask'
import { VueMaskFilter } from 'v-mask'
import { mapState } from 'vuex'
import { toLocaleFixed } from '@/utils'
import ResourcePoolSelector from '@/modules/user/components/ResourcePoolSelector.vue'

export default {
    name: 'CreditTransferDialog',
    components: { ResourcePoolSelector },
    props: {
        credits: { type: Number, required: true },
        precision: { type: [Number, String], default: 2 }
    },
    data() {
        return {
            transferDialog: false,
            transferRecipient: null,
            amount: '',
            transferringCredits: false,
            currencyMask: createNumberMask({ prefix: '', allowDecimal: true, decimalLimit: Number(this.precision) })
        }
    },
    computed: {
        ...mapState('userStore', ['rpManager', 'managedResourcePools']),
        roundedBalance() {
            return Number(this.credits.toFixed(Number(this.precision)))
        },
        amountNumber() {
            return Number(this.amount.replace(/,/g, ''))
        },
        transferAmount() {
            return this.amountNumber === this.roundedBalance ? this.credits : this.amountNumber
        },
        canTransfer() {
            return (
                this.rpManager &&
                !this.transferringCredits &&
                !!this.transferRecipient &&
                Number.isFinite(this.amountNumber) &&
                this.amountNumber > 0 &&
                this.amountNumber <= this.roundedBalance
            )
        },
        transferCreditHint() {
            return `Credits left after transfer: AC ${toLocaleFixed(this.credits - this.transferAmount, Number(this.precision))}`
        }
    },
    methods: {
        setAmount(value) {
            this.amount = VueMaskFilter(value, this.currencyMask)
        },
        sendAll() {
            this.setAmount(this.credits.toFixed(Number(this.precision)))
        },
        cancel() {
            this.transferDialog = false
            this.amount = ''
            this.transferRecipient = null
        },
        async transferCredits() {
            if (!this.canTransfer) return
            this.transferringCredits = true
            try {
                await this.$axios.post(`/resource/${this.$route.params.rpid}/transfer`, {
                    target_rpid: this.transferRecipient,
                    amount: this.transferAmount
                })
                this.cancel()
                this.$emit('transfer-success')
                this.$store.dispatch('showSnackBar', {
                    snackBarText: 'Credit transfer was successful!',
                    snackBarTimeout: 10000,
                    snackBarIcon: 'check_circle'
                })
            } catch (error) {
                this.$store.dispatch('showSnackBar', {
                    snackBarText:
                        error?.response?.data?.code === 'resource_pool_transaction_root_mismatch'
                            ? 'Credits cannot be transferred between resource pools in different hierarchy trees because their credits are not interchangeable. Please consult the documentation for more information.'
                            : 'Credit transfer failed.',
                    snackBarTimeout: 10000,
                    snackBarIcon: 'error'
                })
            } finally {
                this.transferringCredits = false
            }
        }
    }
}
</script>
